import { useState } from 'react';
import { Copy, Reply } from 'lucide-react';
import { colorForAgent } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { IconButton } from '../../ui';
import { AgentDot } from '../../../AgentDot';
import { agentShortForm } from '../../../../lib/use-agent-name';
import { type AgentSummary } from '../../../../lib/my-agents';
import type { Message } from '../../../../lib/use-chat';
import { initialsFromName, parseGroupAttribution, resolveGroupMentions } from '../../../../lib/wa-group-message';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { AgentJoinCard } from '../../AgentJoinCard';
import { AgentAtWorkCard } from '../../AgentAtWorkCard';
import { CreditGiftCard } from '../../CreditGiftCard';
import { MilestoneCard } from '../../MilestoneCard';
import { MaestroMark } from '../../MaestroMark';
import { MeetingSummaryCard } from '../MeetingSummaryCard';
import { MeetingNotesCard } from '../MeetingNotesCard';
import { MeetingApprovalCard } from '../MeetingApprovalCard';
import type { MeetingApprovalRow } from '../../../../lib/meeting-approval';
import { ProposalsCard } from '../ProposalsCard';
import type { ProposalRow } from '../../../../lib/proposals-card';
import { hexToRgba } from '../../../../lib/hex-to-rgba';
import { SentAttachmentList } from '../attachments';
import { LinkifiedText } from '../../../LinkifiedText';
import { ContactMentionText } from './ContactMentionText';
import { useTypewriter } from '../loaders';
import { formatMessageTime, rowGapAfter, TRAILING_BLOCK_GAP, voiceTranscript } from '../message-utils';
import { isAudioAttachment } from '../../../../lib/voice-message';
import { MicGlyph } from './MicGlyph';
import { AssistantMarkdownContent } from './AssistantMarkdownContent';
import { ChoiceChips } from './ChoiceChips';
import { SurveyWidget } from './SurveyWidget';
import { CreditsCard } from './CreditsCard';
import { ConnectorConnectCard } from './ConnectorConnectCard';
import { InsightCard } from './InsightCard';
import { OpportunityCard } from './OpportunityCard';
import { OutreachPreviewCard } from './OutreachPreviewCard';
import { ShareOfferCard, ShareResponseRow, ShareSomeoneElse } from './ShareOfferCard';
import { PostConnectedConnectorCard } from './PostConnectedConnectorCard';
import { BandOfferCard } from './BandOfferCard';
import { BandCard } from './BandCard';
import { ChatGoalCard } from './ChatGoalCard';
import { FailedTurnRow } from './FailedTurnRow';
import { QuickReactBar, ReactionChips, ReactToggleButton } from './MessageReactions';
import type { ChoiceOption } from '../../../../lib/message-choices';
import {
  AGENT_BUBBLE,
  ASSISTANT_BODY,
  CARD_WRAP,
  SPEAKER_AVATAR,
  SPEAKER_HEADER,
  SPEAKER_NAME,
  SPEAKER_TITLE,
  TIME_BASE,
  USER_BUBBLE,
  USER_BUBBLE_VOICE,
} from './styles';

// WhatsApp quoted-reply: a quiet quote block above the bubble body. `sender`/`preview` are
// resolved server-side (digest-engine); an unresolved reference renders the generic line.
function ReplyQuote({ replyTo }: { replyTo: NonNullable<Message['replyTo']> }) {
  const { t } = useTranslation();
  return (
    <div style={REPLY_QUOTE}>
      {replyTo.sender && <div style={REPLY_QUOTE_SENDER}>{replyTo.sender}</div>}
      <div dir="auto" style={REPLY_QUOTE_TEXT}>
        {replyTo.preview || t('chat.replyTo.generic')}
      </div>
    </div>
  );
}

const REPLY_QUOTE: React.CSSProperties = {
  borderInlineStart: '3px solid rgba(var(--ink-rgb),0.25)',
  padding: '4px 10px',
  marginBottom: 6,
  borderRadius: 6,
  background: 'rgba(var(--ink-rgb),0.05)',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
};

const REPLY_QUOTE_SENDER: React.CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 700,
  color: 'rgba(var(--ink-rgb),0.75)',
};

const REPLY_QUOTE_TEXT: React.CSSProperties = {
  fontSize: '0.78125rem',
  color: 'rgba(var(--ink-rgb),0.55)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  // Bounded by the bubble (whose max-width is container-capped), NOT a fixed px width — a fixed
  // cap wider than a narrow panel forced the bubble past its container and clipped (RTL panel bug).
  maxWidth: '100%',
};

function ReplyActionButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <IconButton size="small" variant="text" onClick={onClick} aria-label={t('chat.reply.action')} title={t('chat.reply.action')}>
      <Reply size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
    </IconButton>
  );
}

// Copy the message body — an inline toolbar button beside Reply/React (Figma 14692:215825), not a menu
// item. Same ghost skin + hover as the reaction button. Carries the copy behavior the old
// MessageActionsMenu held: clipboard write + the copy_message BI beacon.
function CopyActionButton({ copySource }: { copySource: string }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  return (
    <IconButton
      size="small"
      variant="text"
      onClick={() => {
        logPageClickBi({ page_name: 'chat_home', page_version: 'v3_chat_home', button_name: 'copy_message' });
        navigator.clipboard.writeText(copySource).catch(() => {
          // Clipboard is gated by focus/permission; nothing actionable if it's denied.
        });
      }}
      aria-label={t('common.copy')}
      title={t('common.copy')}
    >
      <Copy size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
    </IconButton>
  );
}

const EMPTY_SELECTED_IDS: ReadonlySet<string> = new Set();

export function MessageRow({
  message,
  agentCardRun,
  tightenGapAfter,
  agent,
  maestroName,
  brief,
  isMaestro,
  briefParticipantIds,
  isSummaryTurn,
  conversationId,
  threadConversationId,
  canSendToChat,
  isWaGroup,
  assistantName,
  participantsByWaId,
  onReply,
  onToggleReaction,
  selectedChoiceIds,
  onSelectChoice,
  onPickBand,
  onShareSomeoneElse,
  onShareSomeoneElseOpened,
  shareSomeoneElseDisabled,
  onRequestConnectorLink,
  onKickOffGoal,
  onConnectorConnected,
  onReopenSurvey,
  alignEdges,
  turnActive,
  meetingApprovalRow,
  proposalRows,
  onRetryFailedTurn,
}: {
  message: Message;
  agentCardRun?: NonNullable<Message['agentCard']>[];
  // This row continues a same-speaker burst — the NEXT message is another plain
  // reply from the same agent with no user turn between them — so the gap below
  // this row tightens instead of using full turn-taking spacing.
  tightenGapAfter?: boolean;
  agent?: AgentSummary;
  maestroName?: string;
  // This row is inside a brief round-table. Gates the brief-only chrome — a sub-agent's turn gets a
  // bordered bubble in its identity colour, the Maestro's turn wears the Maestro mark — matching native
  // (which draws none of this in a normal 1:1 chat even if an `agentId` is present).
  brief?: boolean;
  // This turn is the Maestro/facilitator's, even if it carries an `agentId` (the round-table's framing
  // turns do, but the id isn't a hired agent). Forces the Maestro treatment — mark + plain prose — over
  // the sub-agent bubble. Resolved by the panel against the agent roster.
  isMaestro?: boolean;
  // Distinct agent ids who spoke in this brief, first-spoke order — the MeetingSummaryCard's avatar
  // cluster + "N agents met" count.
  briefParticipantIds?: string[];
  // This row is the round-table's wrap-up — render the MeetingSummaryCard. Resolved by the panel from
  // the server flag OR the brief fetch's raw metadata (the messages API proxies to the pod in dev).
  isSummaryTurn?: boolean;
  // The thread this row belongs to. Only the summary card reads it — the questions the meeting raised
  // hang off the conversation, and answering them there is what stops the next brief re-asking.
  /** The thread this row belongs to, always set: what the attachments settle a rendering image's game against. */
  threadConversationId?: string;
  conversationId?: string;
  /** Whether a send into this thread can actually happen NOW — false while a turn is streaming, which
   *  `send` refuses. Only the meeting-notes card reads it, to disable its task actions rather than let a
   *  press look like it worked. Absent means yes. */
  canSendToChat?: boolean;
  // WhatsApp GROUP conversation: inbound member turns carry a `[Name (waId)]:`
  // attribution prefix that we decode into a sender avatar + name (display-only).
  isWaGroup?: boolean;
  assistantName?: string;
  participantsByWaId?: Map<string, string>;
  // Start a quoted reply to this message — shown as a hover action on user/assistant rows
  // (and WA-group member rows) when the surface supports composing.
  onReply?: (message: Message) => void;
  // Toggle the user's own emoji reaction on this message (quick bar + chips).
  // NOT gated on locked channels — reactions are in-app annotations.
  onToggleReaction?: (message: Message, emoji: string) => void;
  // Option ids already answered for this row's structured ask (derived from the
  // transcript's USER choiceAnswer rows) — those chips render selected.
  selectedChoiceIds?: ReadonlySet<string>;
  // Tap a choice chip → send the option's label as a normal user turn carrying
  // the structured answer. Absent (read-only surface) => inert chips.
  onSelectChoice?: (message: Message, option: ChoiceOption) => void;
  /** "Send to someone else" on a share offer: the person the user typed. Absent on a channel-locked
   *  thread, same as `onSelectChoice`. Returns whether the send was taken, so the field only closes on one. */
  onShareSomeoneElse?: (message: Message, recipient: { email: string }) => boolean;
  /** The share offer's email field was revealed. */
  onShareSomeoneElseOpened?: (message: Message) => void;
  /** A reply is streaming: the control is shown but not actionable (see `ShareSomeoneElse`). */
  shareSomeoneElseDisabled?: boolean;
  /** Tap a band on the offer card. Absent on a channel-locked thread, same as `onSelectChoice`. */
  /** Returns whether the pick was taken; the card opens the band's connect walk only when it was. */
  onPickBand?: (option: { id: string; name: string }) => boolean;
  // "Get a new link" on an expired connector card → send the localized ask as a
  // normal user turn. Absent (read-only surface) => the card shows the line only.
  onRequestConnectorLink?: (message: Message, text: string) => void;
  // "Kick off" on a goal card → send its localized line as a normal user turn, exactly as if typed.
  // Absent (read-only surface) => the card shows no button.
  onKickOffGoal?: (message: Message, text: string) => void;
  // The card observed its connect: the genome is about to wake the loop, so the transcript shows a
  // thinking line until the harness sends the real one (see lib/connect-handoff).
  onConnectorConnected?: (name: string) => void;
  // Take back an ask the reader closed with the X: the prompts below the reply become the way back
  // into the card. Passed ONLY for the row whose ask is still the current one (nothing said past it),
  // because a row that cannot reopen anything must not read as clickable — see `reopenableSurveyAskId`
  // in MessagesPanel.
  onReopenSurvey?: () => void;
  // Full-screen desktop chat: inset the whole row (user bubble + assistant message) by
  // ROW_EDGE_INSET from the reading column's edges, so both land the same distance from it
  // (narrow/docked keep no inset — a slight zig-zag, by design there).
  alignEdges?: boolean;
  // This row is the one currently being written: the panel's own SSE is open AND this is the newest
  // row. ANDed with the row's `streamOpen` to decide `isCursorOn`, as the safety net for a stream cut
  // without a `messageEnd` — that row keeps `streamOpen` until the next block opens, and on its own
  // would read as still streaming the next time anything happened in the conversation.
  turnActive?: boolean;
  /** The live decision behind this row's `meetingApproval` marker, once the panel has read it. Absent
   *  until then, and for every other row — the marker alone never renders buttons. */
  meetingApprovalRow?: MeetingApprovalRow;
  /** The live proposals behind this row's `proposalsCard` marker, in marker order. Absent until read,
   *  and when none matched — the marker alone never renders checkboxes. */
  proposalRows?: ProposalRow[];
  /** "Try again" on a failed-turn row. Passed ONLY for the newest failure with nothing said since, on a
   *  thread that accepts sends — see `failedTurnRetryTarget` in lib/failed-turn.ts. */
  onRetryFailedTurn?: () => void;
}) {
  const { t } = useTranslation();
  const isUser = message.role === 'USER';
  // Both assistant-side roles. An `ASSISTANT_STEP` row — prose the agent wrote in the same step it
  // called a tool — is a message the reader already watched stream in, so it renders as the bubble it
  // was, with the same timestamp, copy button and reply affordance as any other agent turn. (It was
  // `ASSISTANT` only until #7461 hid it; that was reversed 2026-08-27 — see `isVisibleMessage`.)
  // `THINKING` is NOT here: tool-authored status never reaches a row.
  const isAssistant = message.role === 'ASSISTANT' || message.role === 'ASSISTANT_STEP';
  // Per-thread dismissal of the credits card. Local rather than persisted: the card lives in the
  // transcript, so "not now" hides THAT card rather than silencing a global prompt.
  const [creditsCardDismissed, setCreditsCardDismissed] = useState(false);
  // The quick-react emoji bar toggles open under the tool bar when the smiley is clicked
  // (Figma 14511:160345 "When Click on Emoji icon"); picking an emoji closes it.
  const [reactOpen, setReactOpen] = useState(false);
  const rawContent = message.content || '';
  const transcript = voiceTranscript(rawContent);
  const isVoice = transcript !== null;
  const content = transcript ?? rawContent;
  // A voice note carries its clip as an audio attachment — the player renders
  // ABOVE the (dimmed) transcript and replaces the gutter mic marker, which
  // stays only for player-less voice-call turns.
  const hasAudioAttachment = !!message.attachments?.some((a) => isAudioAttachment(a.mimeType));
  // Hooks must run on EVERY render, before the card/group early returns below —
  // `isWaGroup` settles async (the conversations query), so a row can re-render
  // from the plain branch into the group branch; a hook below a conditional
  // return would then change the hook count and crash the tree (React #300).
  const displayed = useTypewriter(content, isUser || !message.animate);
  // The FIRST message of a run of consecutive agentCard messages carries the
  // whole run's agents; the card self-plays once for the whole group.
  if (message.agentCard) {
    return (
      <div style={CARD_WRAP} data-msg-id={message.id}>
        <AgentJoinCard agents={agentCardRun?.length ? agentCardRun : [message.agentCard]} />
      </div>
    );
  }
  // The live at-work card. One card per row, with NO run consolidation — the deliberate opposite of
  // the join card above. Consecutive `agentCard` rows are one hiring moment and fold into a single
  // orbit; consecutive at-work rows are DIFFERENT TASKS, and folding them would hide every task but one.
  // A turn that failed: the harness's canned English apology (`metadata.systemError`) is replaced by the
  // failed-turn row, so the reader can tell the turn died and retry it in one tap.
  if (message.systemError && isAssistant) {
    return <FailedTurnRow messageId={message.id} onRetry={onRetryFailedTurn} />;
  }
  if (message.agentAtWorkCard) {
    return (
      <div style={CARD_WRAP} data-msg-id={message.id}>
        <AgentAtWorkCard card={message.agentAtWorkCard} />
      </div>
    );
  }
  if (message.milestoneCard) {
    return (
      <div style={CARD_WRAP} data-msg-id={message.id}>
        <MilestoneCard card={message.milestoneCard} />
      </div>
    );
  }
  if (message.creditGiftCard) {
    return (
      <div style={CARD_WRAP} data-msg-id={message.id}>
        <CreditGiftCard card={message.creditGiftCard} />
      </div>
    );
  }
  // A recipient's reaction renders as its own row IN PLACE OF a bubble, the same way the meeting
  // summary below does. The row's `content` carries the same sentence, deliberately: a build that has
  // never heard of this key then shows the reaction as a plain assistant line instead of nothing at
  // all — and a build that HAS heard of it must not print the sentence twice, which is what replacing
  // the bubble buys. There is no agent turn behind a bare emoji, so this is not a reply and must not
  // read as one.
  if (message.shareResponseCard) {
    return (
      <div style={CARD_WRAP} data-msg-id={message.id}>
        <ShareResponseRow row={message.shareResponseCard} />
      </div>
    );
  }
  // The brief round-table's wrap-up turn renders as the meeting summary card in place of a bubble
  // (matching iOS/Android). The avatar cluster + count come from the thread's participants.
  if (message.agentMeetingSummary || isSummaryTurn) {
    return (
      <div style={CARD_WRAP} data-msg-id={message.id}>
        <MeetingSummaryCard
          content={content}
          createdDate={message.createdDate}
          agentIds={briefParticipantIds ?? []}
          conversationId={conversationId}
        />
      </div>
    );
  }
  // A meeting Maestro sat in: the write-up row renders as the notes card, drawn from the
  // `notes.md` attached to it. Its files still list in the chat's Files panel; the card is the row.
  if (message.meetingNotesCard) {
    return (
      <div style={CARD_WRAP} data-msg-id={message.id}>
        <MeetingNotesCard message={message} conversationId={conversationId} canSend={canSendToChat !== false} />
      </div>
    );
  }
  const toggleReaction = onToggleReaction ? (emoji: string) => onToggleReaction(message, emoji) : undefined;
  // WhatsApp GROUP member turn: decode the server's `[Name (waId)]:` attribution
  // into a group-chat row — sender avatar + name, body with mentions resolved,
  // the prefix hidden. Gated on `isWaGroup` so 1:1 rendering is never touched.
  const groupAttribution = isWaGroup ? parseGroupAttribution(message.content) : null;
  if (groupAttribution) {
    const time = formatMessageTime(message.createdDate);
    const resolvedText = resolveGroupMentions(groupAttribution.text, {
      assistantName: assistantName || t('chat.agentFallback'),
      participantsByWaId,
    });
    // A group member's voice note arrives voice-wrapped too — same display
    // rule: player (attachment) above, bare transcript below, dimmed.
    const groupTranscript = voiceTranscript(resolvedText);
    const text = groupTranscript ?? resolvedText;
    return (
      <div
        data-msg-id={message.id}
        className="orion-msg-row"
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: 24 }}
      >
        <div style={SPEAKER_HEADER}>
          <span aria-hidden style={{ ...SPEAKER_AVATAR, background: colorForAgent(groupAttribution.waId) }}>
            {initialsFromName(groupAttribution.displayName)}
          </span>
          <span style={SPEAKER_NAME}>{groupAttribution.displayName}</span>
        </div>
        <div style={{ display: 'flex', width: '100%', maxWidth: '100%' }}>
          <div style={USER_BUBBLE}>
            {message.replyTo && <ReplyQuote replyTo={message.replyTo} />}
            {groupTranscript !== null && message.attachments && message.attachments.length > 0 && (
              <SentAttachmentList attachments={message.attachments} tone="bubble" />
            )}
            <span
              dir="auto"
              style={{ whiteSpace: 'pre-wrap', display: 'block', ...(groupTranscript !== null ? { opacity: 0.72, marginTop: 8 } : {}) }}
            >
              <LinkifiedText text={text} />
            </span>
            {groupTranscript === null && message.attachments && message.attachments.length > 0 && (
              <SentAttachmentList attachments={message.attachments} tone="bubble" />
            )}
            {time && <div style={{ ...TIME_BASE, color: 'var(--ds-text-secondary)' }}>{time}</div>}
          </div>
        </div>
        <ReactionChips reactions={message.reactions} onToggle={toggleReaction} overlapBubble />
        {(toggleReaction || onReply) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {onReply && <ReplyActionButton onClick={() => onReply(message)} />}
            {toggleReaction && <ReactToggleButton open={reactOpen} onToggle={() => setReactOpen((o) => !o)} />}
          </div>
        )}
        {toggleReaction && reactOpen && (
          <div style={{ marginTop: 12 }}>
            <QuickReactBar
              onToggle={(emoji) => {
                toggleReaction(emoji);
                setReactOpen(false);
              }}
            />
          </div>
        )}
      </div>
    );
  }
  const time = formatMessageTime(message.createdDate);
  // The caret marks "the reply is still being written", which is TWO things: the server is still
  // appending to this row (`streamOpen`), or the typewriter has not finished revealing what already
  // arrived. It used to be the second alone — and that one flaps, because the typewriter (212-515
  // chars/s) outruns the SSE feed and catches up between almost every delta. So the caret mounted and
  // unmounted tens of times a second, each toggle adding/removing a 24px line box under the prose
  // (see `AssistantMarkdownContent`) and jerking the whole bottom-pinned transcript by that much —
  // measured on the reported recording as nine one-frame ±23px jumps of the entire chat. `streamOpen`
  // holds steady for the row's whole stream, so the caret now mounts once and unmounts once.
  const isCursorOn = !isUser && message.animate && ((!!message.streamOpen && !!turnActive) || displayed.length < content.length);
  // Message-level copy sits in the "more" menu — suppressed while streaming and when the body has
  // code fences (they carry their own per-block copy).
  const showCopy = isAssistant && !isVoice && !isCursorOn && !content.includes('```');
  // A sub-agent's round-table turn (e.g. a brief) is attributed to that agent;
  // color is always by id, name/title from the roster. Absent => the Maestro's
  // turn, which is ALSO labeled when `maestroName` is set (brief group-chat).
  const attributedAgentId = isAssistant && !isMaestro ? message.agentId : undefined;
  const speaker = attributedAgentId
    ? { color: colorForAgent(attributedAgentId), name: agent?.name || t('chat.agentFallback'), title: agent?.title, initial: agent?.name?.[0] }
    : isAssistant && maestroName
    ? { color: 'var(--accent-solid)', name: maestroName, title: undefined, initial: agentShortForm(maestroName)[0] }
    : undefined;
  // Whether this row DRAWS a bubble (the same branch the body style takes below) — a user turn, or a
  // sub-agent's brief turn. Assistant prose flows bare on the surface, so it has no edge for the
  // reaction pill to sit on.
  const hasBubble = isUser || !!(brief && attributedAgentId);
  // What this row draws BELOW its bubble — the chips, and either a brief's bare timestamp or the
  // reply/copy/react tool bar. A row carrying one keeps the full turn gap; see `rowGapAfter`.
  const hasReactionChips = (message.reactions?.length ?? 0) > 0;
  const hasToolbar = isAssistant && (brief ? !!time : !!(time || showCopy || onReply || toggleReaction));
  return (
    <div
      data-msg-id={message.id}
      className={isAssistant || isUser ? 'orion-msg-row' : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        marginBottom: rowGapAfter(!!tightenGapAfter, hasReactionChips || hasToolbar),
      }}
    >
      {speaker && (
        // Brief turns sit 12 below the speaker label (Figma "Morning Brief" itemSpacing 12); the
        // narrower non-brief speaker label keeps its 6.
        <div style={brief ? { ...SPEAKER_HEADER, marginBottom: 12 } : SPEAKER_HEADER}>
          {/* A sub-agent wears its bare multi-band identity dot (native's `AgentMark` — no letter
              overlaid); the Maestro wears its reserved brand mark in a brief (matching native), else
              the solid `--accent-solid` circle with its initial. */}
          {attributedAgentId ? (
            <AgentDot id={attributedAgentId} size={brief ? BRIEF_AVATAR : (SPEAKER_AVATAR.width as number)} />
          ) : brief ? (
            <MaestroMark size={BRIEF_AVATAR} />
          ) : (
            <span aria-hidden style={{ ...SPEAKER_AVATAR, background: speaker.color }}>
              {(speaker.initial ?? '·').toUpperCase()}
            </span>
          )}
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
            <span style={SPEAKER_NAME}>{speaker.name}</span>
            {/* "Name • Role" — the bullet + role in the secondary ink (Figma "Morning Brief"). */}
            {speaker.title && (
              <span style={SPEAKER_TITLE}>
                <span aria-hidden style={{ marginInlineEnd: 4 }}>
                  •
                </span>
                {speaker.title}
              </span>
            )}
          </span>
        </div>
      )}
      {/* Every card a reply rides sits ABOVE the reply's own words: the card is the thing being
          shown, and the words are the agent asking about it. Reading order is card, then question,
          then the choice chips that answer it — so the chips stay BELOW the text, still under their
          own card. The connect card is the exception: it renders under the words (see below). */}
      {isAssistant && message.insightCard && <InsightCard card={message.insightCard} conversationId={conversationId} />}
      {isAssistant && message.opportunityCard && <OpportunityCard card={message.opportunityCard} />}
      {/* Two whole bands to choose between. Content only — the pick is this row's own chips below, which
          is why nothing here is tappable. */}
      {isAssistant && message.bandOfferCard && (
        <BandOfferCard card={message.bandOfferCard} conversationId={conversationId} onPick={onPickBand} />
      )}
      {/* The band as created: the team, and one collapsed row per app that opens onto its routines. One
          card in place of the N connector cards the connect chain would otherwise stack under this reply. */}
      {isAssistant && message.bandCard && <BandCard card={message.bandCard} conversationId={conversationId} onConnected={onConnectorConnected} />}
      {/* What a connected app brought them, with a switch per automation. Below the band card, so the
          team comes first and the routines it runs follow. The reply's own words still say what the
          connection brought, because a client that cannot render this shows only those words. */}
      {isAssistant && message.postConnectedConnectorCard && (
        // The frame's own 680 (Figma 18942-127886), flush with the row like the band card it stacks under
        // after a band connect. The reading-column inset the connect card takes would squeeze it to 640
        // in the expanded chat, and leave its edge 40px off the band card's.
        <div style={{ boxSizing: 'border-box', width: '100%', maxWidth: 680 }}>
          <PostConnectedConnectorCard card={message.postConnectedConnectorCard} />
        </div>
      )}
      {/* "Running low on credits" card — rides the reply, so the assistant's answer is never replaced
          by the ask. Dismissing hides it for this thread only. */}
      {isAssistant && message.creditsCard && !creditsCardDismissed && (
        <CreditsCard card={message.creditsCard} onDismiss={() => setCreditsCardDismissed(true)} />
      )}
      {/* The outreach preview: what each contact will receive, drawn for its channel. The reply below
          it asks whether to send; the chips under that are its Send / Edit actions. */}
      {isAssistant && message.outreachPreviewCard && <OutreachPreviewCard card={message.outreachPreviewCard} />}
      {/* The share offer: the card as the recipient meets it. It renders NO buttons — the names are this
          row's ordinary choice chips below, which is what lets an app that has never heard of the card
          key still send. See ShareOfferCard's own doc. */}
      {isAssistant && message.shareOfferCard && <ShareOfferCard card={message.shareOfferCard} />}
      <div
        style={{
          display: 'flex',
          flexDirection: isUser ? 'row-reverse' : 'row',
          alignItems: 'flex-start',
          // Figma Spacing/10 between the voice icon and the bubble (node 14511:160571).
          gap: 10,
          // Span the row so the bubble's width/max-width resolve against the full
          // row; without this the wrapper collapses and a short message wraps.
          width: '100%',
          maxWidth: '100%',
          // Full-screen desktop chat (Monday 12819056547 "[Desktop] Paddings", Figma
          // "Spacing/40"): both edges of the row carry a 40px inset from the reading
          // column, so the user bubble (flush to the row's own end) and the assistant
          // message (flush to the row's own start) land the SAME distance from the
          // column edges — that's what "aligned" means here, not zero-gap/flush.
          // Narrow/docked keep no inset (unchanged). No global border-box reset in this app, so
          // the row needs box-sizing set explicitly here — otherwise the inset padding would add
          // to the 100% width and overflow the 720px reading column by 80px.
          ...(alignEdges ? { boxSizing: 'border-box', paddingInlineStart: ROW_EDGE_INSET, paddingInlineEnd: ROW_EDGE_INSET } : {}),
        }}
      >
        {/* Voice marker: the DS mic icon button in the side gutter, centered on the first
            text line. Voice-CALL turns only — a voice NOTE's audio attachment renders
            the inline player instead. */}
        {isVoice && !hasAudioAttachment && (
          <span style={{ marginTop: isUser ? 3 : 0, display: 'inline-flex' }}>
            <MicGlyph title={isUser ? t('chat.voice.youSpoke') : t('chat.voice.saidOnCall')} />
          </span>
        )}
        <div
          style={
            isUser
              ? isVoice
                ? USER_BUBBLE_VOICE
                : USER_BUBBLE
              : brief && attributedAgentId
              ? { ...AGENT_BUBBLE, borderColor: hexToRgba(colorForAgent(attributedAgentId), 0.5) }
              : ASSISTANT_BODY
          }
        >
          {isUser && message.replyTo && <ReplyQuote replyTo={message.replyTo} />}
          {/* Voice note: player first, bare transcript below it, slightly dimmed. */}
          {isVoice && message.attachments && message.attachments.length > 0 && (
            <SentAttachmentList
              attachments={message.attachments}
              tone={isUser ? 'bubble' : 'surface'}
              conversationId={threadConversationId}
              messageId={message.id}
              createdAt={message.createdDate}
            />
          )}
          {isVoice ? (
            <span dir="auto" style={{ whiteSpace: 'pre-wrap', display: 'block', opacity: 0.72, ...(hasAudioAttachment ? { marginTop: 8 } : {}) }}>
              <LinkifiedText text={displayed} />
            </span>
          ) : isAssistant ? (
            <AssistantMarkdownContent displayed={displayed} isCursorOn={!!isCursorOn} />
          ) : (
            // display:block so dir="auto" also sets the line alignment — an inline span only
            // reorders bidi runs and leaves RTL text ragged-left inside the bubble.
            <span dir="auto" style={{ whiteSpace: 'pre-wrap', display: 'block' }}>
              {/* A picked contact's `[Name]` token renders as a bold `@Name` mention here. */}
              <ContactMentionText text={displayed} />
            </span>
          )}
          {!isVoice && message.attachments && message.attachments.length > 0 && (
            <SentAttachmentList
              attachments={message.attachments}
              tone={isUser ? 'bubble' : 'surface'}
              conversationId={threadConversationId}
              messageId={message.id}
              createdAt={message.createdDate}
            />
          )}
          {/* User keeps its timestamp inside the bubble; the assistant's moves to
              the tool bar row below (right-aligned). The voice bubble sets the mock's
              12px text→time gap and Tiny/Medium -3% tracking (Figma 14511:160571). */}
          {isUser && time && (
            <div
              style={{
                ...TIME_BASE,
                color: 'var(--ds-text-secondary)',
                ...(isVoice ? { marginTop: 12, letterSpacing: '-0.03em' } : {}),
              }}
            >
              {time}
            </div>
          )}
        </div>
      </div>
      {/* The owner's Approve / Decline for a question a guest asked in a meeting — under the prose that
          asks it, in the same reading column. */}
      {isAssistant && meetingApprovalRow && (
        <div
          style={{
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: alignEdges ? 720 + ROW_EDGE_INSET * 2 : 720,
            paddingInlineStart: alignEdges ? ROW_EDGE_INSET : 0,
            paddingInlineEnd: alignEdges ? ROW_EDGE_INSET : 0,
          }}
        >
          <MeetingApprovalCard row={meetingApprovalRow} />
        </div>
      )}
      {/* The proposed tasks this reply offers, one checkbox each — under the prose that offers them. */}
      {isAssistant && proposalRows && proposalRows.length > 0 && (
        <div
          style={{
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: alignEdges ? 720 + ROW_EDGE_INSET * 2 : 720,
            paddingInlineStart: alignEdges ? ROW_EDGE_INSET : 0,
            paddingInlineEnd: alignEdges ? ROW_EDGE_INSET : 0,
          }}
        >
          <ProposalsCard rows={proposalRows} />
        </div>
      )}
      {/* Connector connect card — the reply text carries no URL, this card IS the connect
          affordance (arrives in-band at messageEnd, like choices). The one card that sits UNDER the
          words rather than above them: the words say what to connect and why, then the card hands over
          the button, then the chips offer the alternatives. Same order on iOS and Android. */}
      {isAssistant && message.connectorCard && (
        // The card renders OUTSIDE the reply's own inset body, so confine it to the SAME box the prose
        // uses (`ASSISTANT_BODY`: maxWidth 720, plus the fullscreen `alignEdges` 40px reading-column inset)
        // — otherwise the full-width card overflows the text column on both edges in the expanded chat.
        <div
          style={{
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: alignEdges ? 720 + ROW_EDGE_INSET * 2 : 720,
            paddingInlineStart: alignEdges ? ROW_EDGE_INSET : 0,
            paddingInlineEnd: alignEdges ? ROW_EDGE_INSET : 0,
          }}
        >
          <ConnectorConnectCard
            card={message.connectorCard}
            messageId={message.finalId ?? message.id}
            onRequestNewLink={onRequestConnectorLink ? (text) => onRequestConnectorLink(message, text) : undefined}
            onConnected={onConnectorConnected}
          />
        </div>
      )}
      {/* The goal card sits UNDER the words: it answers the question the reply asks ("ready to set the team
          to work?") and on the completion turn follows the wrap-up it celebrates. */}
      {isAssistant && message.goalCard && (
        <ChatGoalCard
          card={message.goalCard}
          conversationId={conversationId}
          onKickOff={onKickOffGoal ? (text) => onKickOffGoal(message, text) : undefined}
          canSend={canSendToChat !== false}
        />
      )}
      {isAssistant && message.choices && (
        <ChoiceChips
          choices={message.choices}
          selectedIds={selectedChoiceIds ?? EMPTY_SELECTED_IDS}
          onSelect={onSelectChoice ? (option) => onSelectChoice(message, option) : undefined}
        />
      )}
      {/* Below the name chips, as on both phones. No handler on a channel-locked thread → no control. */}
      {isAssistant && message.shareOfferCard?.someoneElse && !message.shareOfferCard.previewOnly && onShareSomeoneElse && (
        <ShareSomeoneElse
          card={message.shareOfferCard}
          onSend={(recipient) => onShareSomeoneElse(message, recipient)}
          onOpened={onShareSomeoneElseOpened ? () => onShareSomeoneElseOpened(message) : undefined}
          disabled={shareSomeoneElseDisabled}
        />
      )}
      {/* The transcript keeps the RECORD of what was asked — the prompts alone. The answerable card
          itself stands in the COMPOSER's place (MessagesPanel), so an ask cannot be both a thing you
          scroll away from and the thing you are being asked right now. Passing no `onSubmit` is what
          settles it. */}
      {isAssistant && message.survey && <SurveyWidget survey={message.survey} onReopen={onReopenSurvey} />}
      <ReactionChips
        reactions={message.reactions}
        onToggle={toggleReaction}
        inset={alignEdges ? ROW_EDGE_INSET : 0}
        overlapBubble={hasBubble}
      />
      {/* A brief turn keeps the clean round-table look (Figma "Morning Brief"): just the timestamp,
          left-aligned directly below the message — no copy/reply/react tool bar. */}
      {isAssistant && brief
        ? time && (
            // 12 below the bubble, matching the 12 above it (Figma "Morning Brief" itemSpacing 12).
            <div style={{ ...TIME_BASE, marginTop: 12, textAlign: 'start', color: 'var(--ds-text-secondary)' }}>{time}</div>
          )
        : /* Assistant tool bar: inline reply · copy · react buttons on the left (gap 4), timestamp on
             the right, 12px below the message — always visible, as the mock shows it (a user's own
             message has no tool bar). Reply and Copy are their own icon buttons, not a "more" menu
             (Figma 14692:215825). Clicking react opens the quick-react emoji bar as a second row below. */
          isAssistant &&
          (time || showCopy || onReply || toggleReaction) && (
            <>
              <div
                style={
                  alignEdges
                    ? {
                        ...TOOLBAR_ROW,
                        boxSizing: 'border-box',
                        paddingInlineStart: ROW_EDGE_INSET,
                        paddingInlineEnd: ROW_EDGE_INSET,
                      }
                    : TOOLBAR_ROW
                }
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {onReply && <ReplyActionButton onClick={() => onReply(message)} />}
                  {showCopy && <CopyActionButton copySource={content} />}
                  {toggleReaction && <ReactToggleButton open={reactOpen} onToggle={() => setReactOpen((o) => !o)} />}
                </div>
                {time && <div style={{ ...TIME_BASE, marginTop: 0, color: 'var(--ds-text-secondary)' }}>{time}</div>}
              </div>
              {toggleReaction && reactOpen && (
                // The opened bar takes the row's reading-column inset too, like the chips and the
                // tool bar above it — otherwise the controls for THIS message start 40px left of it.
                <div style={{ marginTop: 12, ...(alignEdges ? { paddingInlineStart: ROW_EDGE_INSET, paddingInlineEnd: ROW_EDGE_INSET } : null) }}>
                  <QuickReactBar
                    onToggle={(emoji) => {
                      toggleReaction(emoji);
                      setReactOpen(false);
                    }}
                  />
                </div>
              )}
            </>
          )}
    </div>
  );
}

// Brief speaker avatar — 28px (Figma "Morning Brief"), a touch larger than the 24px WA-group avatar.
const BRIEF_AVATAR = 28;

// Full-screen desktop chat's inline inset off the reading column edges (Figma "Spacing/40",
// Monday 12819056547 "[Desktop] Paddings" — a follow-up correction to the original alignEdges
// pass, which had dropped the assistant body's trailing inset to 0 instead of this value).
const ROW_EDGE_INSET = 40;

const TOOLBAR_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  // Figma "Full screen / Chat Layout" QA (Monday 12819150619): 12px between the answer and its
  // reply/copy/react toolbar — was 8. This is the bond `rowGapAfter` keeps the row's own gap clear of.
  marginTop: TRAILING_BLOCK_GAP,
  width: '100%',
  maxWidth: 720,
};
